import { AttachmentForm } from 'Components/_NewElements/GeneralForms/AttachmentsForm';
import ConfirmationDialog from 'Components/ConfirmationDialog';
import React from 'react';
import { useIntl } from 'react-intl';
import { actions } from 'Store/documents/actions';
import { useAppDispatch } from 'Store/index';
import { getLocalizedSubject } from './EditDialog';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import { subjectOfTreatmentNames } from 'Routes/DoctorsOrders/Document/config';

const AddNewDialog = ({ newAttachments, setNewAttachments, handleFileChange, subjectOfTreatment }: IAddNewDialog) => {
  const { formatMessage, messages } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const [loading, setLoading] = React.useState<boolean>(false);

  const dispatch = useAppDispatch();

  const getLocalizedSubjectName = getLocalizedSubject(messages as Record<string, string>);

  const [newData, setNewData] = React.useState<{ subjectName?: string; other?: string; fileName?: string }>({});

  const referenceType = newData['other'] ?? newData['subjectName'] ?? null;

  return (
    <ConfirmationDialog
      open={!!newAttachments}
      fullWidth={false}
      width="lg"
      text={
        <div style={{ minWidth: '70rem' }}>
          {newAttachments && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <AttachmentForm
                fm={fm}
                attachmentData={newAttachments}
                handleFileChange={handleFileChange}
                allowedTypes={['.jpg', '.jpeg', '.png']}
                attachmentLimit={1}
              />
            </div>
          )}

          <div style={{ marginTop: '2rem', fontSize: '1.6rem' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
              <div style={{ width: '60rem' }}>
                <FormRow title="rtms.subjectOfTreatment.name">
                  <InputHandler
                    editing
                    name="subjectName"
                    type="Select"
                    options={[...subjectOfTreatmentNames, 'supportPicture']}
                    optionFormatter={o =>
                      o ? (
                        <React.Fragment key={fm(`rtms.subjectOfTreatmentNames.${o}`)}>
                          {fm(`rtms.subjectOfTreatmentNames.${o}`)}
                        </React.Fragment>
                      ) : (
                        ''
                      )
                    }
                    formData={{
                      document: {
                        subjectName: newData?.['subjectName'] || subjectOfTreatment,
                      },
                      onChange: values => {
                        const value = values['subjectName'] as string;
                        // Reset the other field of the subject selection changes to something else
                        const other =
                          newData['subjectName'] === 'other' && value !== 'other' ? { other: undefined } : {};
                        setNewData({ ...newData, ...other, subjectName: value });
                      },
                    }}
                    placeholder={'general.choose'}
                  />
                </FormRow>
              </div>
            </div>
            {newData['subjectName'] === 'other' && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                <div style={{ width: '60rem' }}>
                  <FormRow title="rtms.subjectOfTreatmentNames.other">
                    <InputHandler
                      editing
                      name="other"
                      type="TextField"
                      formData={{
                        document: { other: newData['other'] || subjectOfTreatment },
                        onChange: values => {
                          const value = values['other'] as string;
                          setNewData({ ...newData, other: value });
                        },
                      }}
                      placeholder={'rtms.subjectOfTreatment.name'}
                    />
                  </FormRow>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
              <div style={{ width: '60rem' }}>
                <FormRow title="general.fileName" headerWidth={3}>
                  <InputHandler
                    editing
                    name="fileName"
                    type="TextField"
                    formData={{
                      document: {
                        fileName:
                          newData['fileName'] ??
                          getLocalizedSubjectName(
                            newData?.['subjectName'] ?? newData['other'] ?? subjectOfTreatment ?? '',
                          ),
                      },
                      onChange: values => {
                        const value = values['fileName'] as string;
                        setNewData({ ...newData, fileName: value });
                      },
                    }}
                    placeholder={'general.fileName'}
                    fullWidth
                  />
                </FormRow>
              </div>
            </div>
          </div>
        </div>
      }
      cancel={{
        callback: e => {
          e.stopPropagation();
          setNewAttachments(null);
          setNewData({});
        },
      }}
      confirm={{
        disabled: !((newAttachments || []).length > 0) || !(referenceType || subjectOfTreatment),
        text: 'general.accept',
        loading: loading,
        callback: async e => {
          e.stopPropagation();
          if (!newAttachments || !(referenceType || subjectOfTreatment)) return;
          setLoading(true);
          const date = new Date();
          const filePromises = newAttachments.map(file =>
            dispatch(
              actions.postNewFile(
                file,
                getLocalizedSubjectName((newData['fileName'] ?? (referenceType || subjectOfTreatment))!),
                date.toISOString(),
                'rtms',
                referenceType || subjectOfTreatment,
              ),
            ),
          );

          await Promise.all(filePromises).then(() => {
            setLoading(false);
            setNewAttachments(null);
            setNewData({});
          });
        },
      }}
    />
  );
};

interface IAddNewDialog {
  newAttachments: File[] | null;
  setNewAttachments: (files: File[] | null) => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  subjectOfTreatment?: string;
}

export default AddNewDialog;
